function cleanString(value) {
  return String(value ?? "").trim();
}

function pickFirst(...values) {
  for (const value of values) {
    const normalized = cleanString(value);
    if (normalized) return normalized;
  }
  return "";
}

function joinLocation(...values) {
  const parts = values.map((value) => cleanString(value)).filter(Boolean);
  return parts.join(", ");
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => cleanString(item))
      .filter(Boolean);
  }
  return [];
}

function uniqueStrings(values = []) {
  return [...new Set(values.map((value) => cleanString(value)).filter(Boolean))];
}

export function buildCandidateSequenceItem(row = {}) {
  const skills = uniqueStrings([
    ...asArray(row.skills),
    ...asArray(row.skill),
    ...asArray(row.skill_list),
  ]);

  return {
    entityType: "candidate",
    entityId: row.candidate_id ?? row.id ?? null,
    name: pickFirst(row.full_name, row.candidate_name, row.name),
    title: pickFirst(row.title, row.candidate_title, row.job_title, row.position),
    company: pickFirst(row.company_name, row.company, row.current_company),
    email: pickFirst(
      row.email,
      row.gmail,
      row.gmail_address,
      row.email_address,
      row.primary_email,
      row.personal_email,
      row.candidate_email
    ),
    phone: pickFirst(row.phone, row.phone_number, row.mobile, row.mobile_phone),
    linkedinUrl: pickFirst(
      row.linkedin_url,
      row.linkedin,
      row.linkedinUrl,
      row.linkedin_profile_url
    ),
    location: pickFirst(row.location, joinLocation(row.city, row.country)),
    owner: pickFirst(row.recruiter_name, row.owner, row.owner_name),
    metadata: {
      source: "candidates",
      status: pickFirst(row.candidate_status, row.status, "Available"),
      candidateStatus: pickFirst(row.candidate_status, row.status, "Available"),
      skills,
      avatarUrl: pickFirst(row.avatar_url, row.avatarUrl),
      bestMatchPercentage:
        row.best_match_percentage ??
        row.bestMatchPercentage ??
        row.match_percentage ??
        row.matchPercentage ??
        null,
    },
  };
}

export function buildJobSequenceItem(row = {}, source = "jobs") {
  return {
    entityType: "job",
    entityId: row.job_id ?? row.id ?? null,
    name: pickFirst(row.job_title, row.title, row.name),
    title: pickFirst(
      row.hiring_manager_name,
      row.owner,
      row.owner_name,
      row.recruiter_name
    ),
    company: pickFirst(row.company_name, row.company, row.client_name),
    email: pickFirst(row.contact_email, row.email),
    phone: pickFirst(row.contact_phone, row.phone),
    linkedinUrl: pickFirst(row.linkedin_url, row.linkedin),
    location: pickFirst(
      row.location,
      joinLocation(row.location_city, row.location_state, row.location_country)
    ),
    owner: pickFirst(row.owner, row.owner_name, row.recruiter_name),
    metadata: { source },
  };
}

export function buildClientSequenceItem(row = {}) {
  const companyName = pickFirst(row.name, row.company_name, row.company);
  return {
    entityType: "client",
    entityId: row.client_id ?? row.id ?? null,
    name: companyName,
    title: pickFirst(row.contact, row.contact_name),
    company: companyName,
    email: pickFirst(row.contact_email, row.email),
    phone: pickFirst(row.contact_phone, row.phone),
    linkedinUrl: pickFirst(row.linkedin_url, row.linkedin),
    location: pickFirst(
      row.location,
      joinLocation(row.location_city, row.location_state, row.location_country)
    ),
    owner: pickFirst(row.owner, row.owner_name),
    metadata: { source: "clients" },
  };
}

export function buildLeadSequenceItem(row = {}, source = "leads") {
  const companyName = pickFirst(row.name, row.company_name, row.company);
  return {
    entityType: "lead",
    entityId: row.lead_id ?? row.id ?? null,
    name: companyName,
    title: pickFirst(row.contact_name, row.title),
    company: companyName,
    // Lead outreach belongs to its Contact, rather than a generic company email.
    email: pickFirst(row.contact_email, row.email_url).replace(/^mailto:/i, "").split("?")[0],
    phone: pickFirst(row.phone, row.contact_phone),
    linkedinUrl: pickFirst(row.linkedin_url, row.linkedin),
    location: pickFirst(
      row.location,
      joinLocation(row.location_city, row.location_state, row.location_country)
    ),
    owner: pickFirst(row.owner, row.owner_name),
    metadata: { source },
  };
}

export function buildHiringManagerSequenceItem(row = {}, source = "hiring-managers") {
  const normalizedEntityType = cleanString(row.entity_type).toLowerCase();
  const sequenceEntityType =
    normalizedEntityType === "company" || normalizedEntityType === "client"
      ? "client"
      : "lead";
  const companyName = pickFirst(row.entity_name, row.company_name, row.company);

  return {
    entityType: sequenceEntityType,
    entityId: row.entity_id ?? row.id ?? null,
    name: pickFirst(row.name, row.contact_name),
    title: pickFirst(row.role, row.title, row.position, "Hiring Manager"),
    company: companyName,
    email: pickFirst(row.email, row.contact_email),
    phone: pickFirst(row.phone, row.contact_phone),
    linkedinUrl: pickFirst(row.linkedin_url, row.linkedin),
    location: pickFirst(
      row.location,
      joinLocation(row.location_city, row.location_state, row.location_country)
    ),
    owner: pickFirst(row.owner, row.owner_name),
    metadata: {
      source,
      contactKind: cleanString(row.contact_kind),
      parentEntityType: normalizedEntityType || sequenceEntityType,
      parentEntityName: companyName,
    },
  };
}
